(() => {
  'use strict';
  const normalize = value => String(value ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/([a-z])([0-9])/g, '$1 $2').replace(/([0-9])([a-z])/g, '$1 $2')
    .replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const tokens = value => normalize(value).split(' ').filter(Boolean);
  const isDecant = product => product.is_decant === true || tokens(product.title).some(word => word === 'decant' || word === 'decants');
  const wantsDecants = query => tokens(query).some(word => word === 'decant' || word === 'decants');
  function matches(product, query) {
    const terms = tokens(query);
    if (!terms.length) return false;
    const words = tokens(`${product.title || ''} ${product.vendor || ''}`);
    return terms.every((term, index) => {
      if (words.includes(term)) return true;
      if ((term === 'decant' || term === 'decants') && isDecant(product)) return true;
      if (index === terms.length - 1 && /^[a-z]{2,}$/.test(term) && words.some(word => word.startsWith(term))) return true;
      for (let start = 0; start < words.length; start++) {
        let joined = words[start];
        for (let end = start + 1; end < words.length && joined.length < term.length; end++) {
          joined += words[end];
          if (joined === term) return true;
        }
      }
      return false;
    });
  }
  function rank(products, query) {
    const terms = tokens(query);
    const score = product => {
      const words = tokens(`${product.title || ''} ${product.vendor || ''}`);
      return terms.reduce((sum, term) => sum + (words.includes(term) ? 100 : 0), 0);
    };
    return products.filter(product => matches(product, query))
      .sort((a, b) => (Number(isDecant(a)) - Number(isDecant(b)))
        || score(b) - score(a)
        || Number(b.available) - Number(a.available)
        || String(a.title).localeCompare(String(b.title), 'es')
        || String(a.id).localeCompare(String(b.id)));
  }
  let cached;
  let cachedAt = 0;
  let inFlight;
  async function loadCatalog() {
    if (cached && Date.now() - cachedAt < 60000) return cached;
    if (inFlight) return inFlight;
    inFlight = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const base = new URL(`${root.replace(/\/?$/, '/')}collections/all`, window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) base.searchParams.set('preview_theme_id', preview);
      base.searchParams.set('section_id', 'vap-search-index');
      const fetchPage = async page => {
        const url = new URL(base);
        url.searchParams.set('page', String(page));
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(url.href, {signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: {Accept: 'text/html'}});
          if (!response.ok) throw new Error('No se pudo cargar el catálogo de búsqueda.');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const script = document.querySelector('script[data-vap-search-index]');
          if (!script) throw new Error('El índice de búsqueda no está disponible.');
          const data = JSON.parse(script.textContent);
          if (!Array.isArray(data.products) || data.page !== page || !Number.isInteger(data.pages) || data.pages < 0 || !Number.isInteger(data.total) || data.total < 0) throw new Error('El índice de búsqueda está incompleto.');
          return data;
        } finally { clearTimeout(timer); }
      };
      const first = await fetchPage(1);
      const pages = Math.max(1, first.pages);
      if (pages > 100 || first.total > 25000) throw new Error('El catálogo excede el límite del índice de búsqueda.');
      const products = [...first.products];
      for (let page = 2; page <= pages; page++) {
        const data = await fetchPage(page);
        if (data.pages !== first.pages || data.total !== first.total) throw new Error('El catálogo cambió durante la búsqueda. Volvé a intentar.');
        products.push(...data.products);
      }
      const ids = new Set();
      for (const product of products) {
        if (!product || product.id == null || typeof product.title !== 'string' || typeof product.vendor !== 'string' || typeof product.html !== 'string' || typeof product.url !== 'string' || typeof product.available !== 'boolean') throw new Error('El índice contiene un producto inválido.');
        const id = String(product.id);
        if (ids.has(id)) throw new Error('El índice contiene productos duplicados.');
        ids.add(id);
        const url = new URL(product.url, window.location.origin);
        if (url.origin !== window.location.origin || !/\/products\//.test(url.pathname)) throw new Error('El índice contiene un enlace inválido.');
      }
      if (products.length !== first.total) throw new Error('No se pudo cargar el catálogo completo.');
      cached = Object.freeze(products.map(product => Object.freeze(product)));
      cachedAt = Date.now();
      return cached;
    })();
    try { return await inFlight; } finally { inFlight = undefined; }
  }
  const api = {normalize, matches, rank, filter: rank, isDecant, wantsDecants, loadCatalog};
  if (typeof window !== 'undefined') window.VapStrictSearch = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
